import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { axiosInstance } from "@/pages/auth/auth.context";
import { BackLink } from "@/components/brand/BackLink";

const TABLE_COLUMNS_COUNT = 4;

enum EventTimeframe {
  PAST = "past",
  UPCOMING = "upcoming",
}

interface EventItem {
  eventId: string;
  companyName: string;
  startTime: string;
  eventLength: number;
  address: string;
  isUserRegistered: boolean;
  userPositions: string[];
  totalPositions: number;
  filledPositions: number;
}

export default function EmployeeEvents() {
  const { id: userId, timeframe } = useParams<{
    id: string;
    timeframe: string;
  }>();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId || !timeframe) {
      console.error("Missing required parameters: userId or timeframe");
      navigate("/admin/employees", { replace: true });
      return;
    }

    // Validate timeframe parameter against enum values
    if (!Object.values(EventTimeframe).includes(timeframe as EventTimeframe)) {
      console.error(
        `Invalid timeframe: ${timeframe}. Valid values are: ${Object.values(
          EventTimeframe,
        ).join(", ")}`,
      );
      navigate("/admin/employees", { replace: true });
      return;
    }

    setLoading(true);
    axiosInstance
      .get<EventItem[]>(`/user/${userId}/events`, {
        params: { timeframe: timeframe.toUpperCase() },
      })
      .then((res) => setEvents(res.data))
      .catch((err) => {
        console.error("Failed to fetch employee events:", err);
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [userId, timeframe, navigate]);

  const getPageTitle = useCallback(() => {
    return timeframe === EventTimeframe.PAST
      ? "Employee Work History"
      : "Employee Upcoming Events";
  }, [timeframe]);

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <BackLink to="/admin/employees">Back to Employees</BackLink>
            <h1 className="text-3xl font-bold text-foreground">
              {getPageTitle()}
            </h1>
          </div>
        </div>

        <div className="rounded-md border">
          <Table role="table" aria-label={`Employee ${timeframe} events`}>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Position</TableHead>
                <TableHead className="text-right w-auto">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-muted-foreground">
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_COLUMNS_COUNT}
                    className="text-center py-8"
                  >
                    <div role="status" aria-live="polite">
                      Loading events...
                    </div>
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS_COUNT}>
                    {timeframe === EventTimeframe.PAST
                      ? "No work history found."
                      : "No upcoming events found."}
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => (
                  <TableRow key={event.eventId}>
                    <TableCell>{event.companyName}</TableCell>
                    <TableCell>
                      {new Date(event.startTime).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </TableCell>
                    <TableCell>{event.userPositions.join(", ")}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(`/admin/events/${event.eventId}`)
                        }
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
